import type { EditorState } from 'lexical/LexicalEditorState';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect, useRef } from 'react';
import IMurmurHash from 'imurmurhash';

export type OnChangePluginProps = {
  onChange?: (state: EditorState, hasNodeChanged: boolean) => void;
};

export function OnChangePlugin({ onChange }: OnChangePluginProps) {
  const [editor] = useLexicalComposerContext();

  const readyRef = useRef(false);
  const lastHashRef = useRef<number>(0);

  useEffect(() => {
    const stabilize = async () => {
      // during initial state creation, Lexical may incorrectly mark some nodes as dirty
      // we need to wait until the first editor state fully stabilizes before proceeding
      // advice by ChatGPT
      await Promise.resolve();
      await new Promise((r) => setTimeout(r, 0));

      const state = editor.getEditorState();
      const json = JSON.stringify(state.toJSON());
      // we need hashes because it's simpler to compare them than strings which might be large
      lastHashRef.current = new IMurmurHash(json).result();
      readyRef.current = true;
    };
    stabilize();

    return editor.registerUpdateListener(
      ({ editorState, dirtyElements, dirtyLeaves }) => {
        if (!readyRef.current) return;

        const hasNodeChanged = dirtyElements.size > 0 || dirtyLeaves.size > 0;

        const json = JSON.stringify(editorState.toJSON());
        const hash = new IMurmurHash(json).result();

        if (!hasNodeChanged || hash === lastHashRef.current) {
          onChange?.(editorState, false);
          return;
        }

        lastHashRef.current = hash;
        onChange?.(editorState, true);
      },
    );
  }, [editor, onChange]);

  return null;
}
